import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Label,
} from "@asm/ui";
import { AlertCircle, ArrowLeft, Lock, Mail, KeyRound, Shield, Radar, Smartphone, Copy, Check, CheckCircle2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuthStore } from "@/lib/auth";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [totpSetupCode, setTotpSetupCode] = useState("");
  const [secretCopied, setSecretCopied] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [resetNewPassword, setResetNewPassword] = useState("");
  const { t } = useTranslation("auth");

  const {
    login,
    confirmTotp,
    confirmNewPassword,
    setupTotp,
    forgotPassword,
    confirmPasswordReset,
    resetAuthStep,
    isLoading,
    error,
    clearError,
    isAuthenticated,
    authStep,
    totpSetupSecret,
    resetSuccess,
    pendingEmail,
  } = useAuthStore();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    return () => {
      if (authStep === "idle") {
        resetAuthStep();
      }
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    try {
      await login(email, password);
    } catch {
      // Error handled by store
    }
  };

  const handleNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    try {
      await confirmNewPassword(newPassword);
    } catch {
      // Error handled by store
    }
  };

  const handleTotp = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    try {
      await confirmTotp(totpCode);
    } catch {
      // Error handled by store
    }
  };

  const handleTotpSetup = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    try {
      await setupTotp(totpSetupCode);
    } catch {
      // Error handled by store
    }
  };

  const handleCopySecret = async () => {
    if (totpSetupSecret) {
      await navigator.clipboard.writeText(totpSetupSecret);
      setSecretCopied(true);
      setTimeout(() => setSecretCopied(false), 2000);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    try {
      await forgotPassword(resetEmail);
    } catch {
      // Error handled by store
    }
  };

  const handleConfirmReset = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    try {
      await confirmPasswordReset(resetCode, resetNewPassword);
      setResetCode("");
      setResetNewPassword("");
      setResetEmail("");
    } catch {
      // Error handled by store
    }
  };

  const handleBack = () => {
    resetAuthStep();
    setTotpCode("");
    setTotpSetupCode("");
    setNewPassword("");
    setResetCode("");
    setResetNewPassword("");
    setResetEmail("");
  };

  const handleStartForgotPassword = () => {
    clearError();
    // Pre-fill reset email from login form if available
    setResetEmail(email);
    useAuthStore.setState({ authStep: "forgot_password", resetSuccess: false });
  };

  // TOTP setup form (shown after forced password change when MFA is required)
  if (authStep === "totp_setup") {
    return (
      <Card className="border-border bg-card/80 backdrop-blur animate-fade-in-up shadow-xl shadow-black/5">
        <CardHeader className="space-y-1 text-center pb-4">
          <div className="flex justify-center mb-4">
            <div className="p-3 rounded-xl bg-cyan-500/10 border border-cyan-500/20">
              <Smartphone className="h-6 w-6 text-cyan-400" />
            </div>
          </div>
          <CardTitle className="text-2xl">{t("totpSetup.title")}</CardTitle>
          <CardDescription>{t("totpSetup.description")}</CardDescription>
        </CardHeader>
        <form onSubmit={handleTotpSetup}>
          <CardContent className="space-y-4">
            {error && (
              <div className="p-3 text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                {error}
              </div>
            )}
            {totpSetupSecret && (
              <div className="space-y-2">
                <Label>{t("totpSetup.secretKey")}</Label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 p-3 bg-muted/50 border border-border rounded-lg font-mono text-sm break-all select-all">
                    {totpSetupSecret}
                  </code>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="flex-shrink-0"
                    onClick={handleCopySecret}
                  >
                    {secretCopied ? <Check className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">{t("totpSetup.secretHint")}</p>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="totpSetupCode">{t("totpSetup.code")}</Label>
              <Input
                id="totpSetupCode"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                placeholder="000000"
                value={totpSetupCode}
                onChange={(e) => setTotpSetupCode(e.target.value.replace(/\D/g, ""))}
                required
                autoComplete="one-time-code"
                autoFocus
                className="bg-muted/50 border-border focus:border-cyan-500/50 text-center font-mono text-2xl tracking-[0.5em]"
              />
              <p className="text-xs text-muted-foreground text-center">{t("totpSetup.codeHint")}</p>
            </div>
          </CardContent>
          <CardFooter className="flex flex-col space-y-3">
            <Button
              type="submit"
              className="w-full bg-cyan-500 hover:bg-cyan-400 text-black font-semibold"
              disabled={isLoading || totpSetupCode.length !== 6}
            >
              {isLoading ? t("totpSetup.submitting") : t("totpSetup.submit")}
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="w-full text-muted-foreground hover:text-foreground"
              onClick={handleBack}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t("totpSetup.back")}
            </Button>
          </CardFooter>
        </form>
      </Card>
    );
  }

  // New password form
  if (authStep === "new_password") {
    return (
      <Card className="border-border bg-card/80 backdrop-blur animate-fade-in-up shadow-xl shadow-black/5">
        <CardHeader className="space-y-1 text-center pb-4">
          <div className="flex justify-center mb-4">
            <div className="p-3 rounded-xl bg-cyan-500/10 border border-cyan-500/20">
              <KeyRound className="h-6 w-6 text-cyan-400" />
            </div>
          </div>
          <CardTitle className="text-2xl">{t("newPassword.title")}</CardTitle>
          <CardDescription>{t("newPassword.description")}</CardDescription>
        </CardHeader>
        <form onSubmit={handleNewPassword}>
          <CardContent className="space-y-4">
            {error && (
              <div className="p-3 text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                {error}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="newPassword">{t("newPassword.password")}</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="newPassword"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                  autoFocus
                  className="bg-muted/50 border-border focus:border-cyan-500/50 pl-10"
                />
              </div>
              <p className="text-xs text-muted-foreground">{t("newPassword.passwordHint")}</p>
            </div>
          </CardContent>
          <CardFooter className="flex flex-col space-y-3">
            <Button
              type="submit"
              className="w-full bg-cyan-500 hover:bg-cyan-400 text-black font-semibold"
              disabled={isLoading}
            >
              {isLoading ? t("newPassword.submitting") : t("newPassword.submit")}
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="w-full text-muted-foreground hover:text-foreground"
              onClick={handleBack}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t("newPassword.back")}
            </Button>
          </CardFooter>
        </form>
      </Card>
    );
  }

  // TOTP form
  if (authStep === "totp") {
    return (
      <Card className="border-border bg-card/80 backdrop-blur animate-fade-in-up shadow-xl shadow-black/5">
        <CardHeader className="space-y-1 text-center pb-4">
          <div className="flex justify-center mb-4">
            <div className="p-3 rounded-xl bg-cyan-500/10 border border-cyan-500/20">
              <Shield className="h-6 w-6 text-cyan-400" />
            </div>
          </div>
          <CardTitle className="text-2xl">{t("totp.title")}</CardTitle>
          <CardDescription>{t("totp.description")}</CardDescription>
        </CardHeader>
        <form onSubmit={handleTotp}>
          <CardContent className="space-y-4">
            {error && (
              <div className="p-3 text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                {error}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="totpCode">{t("totp.code")}</Label>
              <Input
                id="totpCode"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                placeholder="000000"
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ""))}
                required
                autoComplete="one-time-code"
                autoFocus
                className="bg-muted/50 border-border focus:border-cyan-500/50 text-center font-mono text-2xl tracking-[0.5em]"
              />
              <p className="text-xs text-muted-foreground text-center">{t("totp.codeHint")}</p>
            </div>
          </CardContent>
          <CardFooter className="flex flex-col space-y-3">
            <Button
              type="submit"
              className="w-full bg-cyan-500 hover:bg-cyan-400 text-black font-semibold"
              disabled={isLoading || totpCode.length !== 6}
            >
              {isLoading ? t("totp.submitting") : t("totp.submit")}
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="w-full text-muted-foreground hover:text-foreground"
              onClick={handleBack}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t("totp.back")}
            </Button>
          </CardFooter>
        </form>
      </Card>
    );
  }

  // Forgot password — enter email to receive reset code
  if (authStep === "forgot_password") {
    return (
      <Card className="border-border bg-card/80 backdrop-blur animate-fade-in-up shadow-xl shadow-black/5">
        <CardHeader className="space-y-1 text-center pb-4">
          <div className="flex justify-center mb-4">
            <div className="p-3 rounded-xl bg-cyan-500/10 border border-cyan-500/20">
              <KeyRound className="h-6 w-6 text-cyan-400" />
            </div>
          </div>
          <CardTitle className="text-2xl">{t("forgotPassword.title")}</CardTitle>
          <CardDescription>{t("forgotPassword.description")}</CardDescription>
        </CardHeader>
        <form onSubmit={handleForgotPassword}>
          <CardContent className="space-y-4">
            {error && (
              <div className="p-3 text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                {error}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="resetEmail">{t("forgotPassword.email")}</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="resetEmail"
                  type="email"
                  placeholder={t("forgotPassword.emailHint")}
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  required
                  autoComplete="email"
                  autoFocus
                  className="bg-muted/50 border-border focus:border-cyan-500/50 pl-10"
                />
              </div>
            </div>
          </CardContent>
          <CardFooter className="flex flex-col space-y-3">
            <Button
              type="submit"
              className="w-full bg-cyan-500 hover:bg-cyan-400 text-black font-semibold"
              disabled={isLoading || !resetEmail}
            >
              {isLoading ? t("forgotPassword.submitting") : t("forgotPassword.submit")}
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="w-full text-muted-foreground hover:text-foreground"
              onClick={handleBack}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t("forgotPassword.back")}
            </Button>
          </CardFooter>
        </form>
      </Card>
    );
  }

  // Reset password — enter code and new password
  if (authStep === "reset_password") {
    return (
      <Card className="border-border bg-card/80 backdrop-blur animate-fade-in-up shadow-xl shadow-black/5">
        <CardHeader className="space-y-1 text-center pb-4">
          <div className="flex justify-center mb-4">
            <div className="p-3 rounded-xl bg-cyan-500/10 border border-cyan-500/20">
              <Lock className="h-6 w-6 text-cyan-400" />
            </div>
          </div>
          <CardTitle className="text-2xl">{t("resetPassword.title")}</CardTitle>
          <CardDescription>
            {t("resetPassword.description", { email: pendingEmail })}
          </CardDescription>
        </CardHeader>
        <form onSubmit={handleConfirmReset}>
          <CardContent className="space-y-4">
            {error && (
              <div className="p-3 text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                {error}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="resetCode">{t("resetPassword.code")}</Label>
              <Input
                id="resetCode"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                placeholder="000000"
                value={resetCode}
                onChange={(e) => setResetCode(e.target.value.replace(/\D/g, ""))}
                required
                autoComplete="one-time-code"
                autoFocus
                className="bg-muted/50 border-border focus:border-cyan-500/50 text-center font-mono text-2xl tracking-[0.5em]"
              />
              <p className="text-xs text-muted-foreground text-center">{t("resetPassword.codeHint")}</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="resetNewPassword">{t("resetPassword.password")}</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="resetNewPassword"
                  type="password"
                  value={resetNewPassword}
                  onChange={(e) => setResetNewPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                  className="bg-muted/50 border-border focus:border-cyan-500/50 pl-10"
                />
              </div>
              <p className="text-xs text-muted-foreground">{t("resetPassword.passwordHint")}</p>
            </div>
          </CardContent>
          <CardFooter className="flex flex-col space-y-3">
            <Button
              type="submit"
              className="w-full bg-cyan-500 hover:bg-cyan-400 text-black font-semibold"
              disabled={isLoading || resetCode.length !== 6 || !resetNewPassword}
            >
              {isLoading ? t("resetPassword.submitting") : t("resetPassword.submit")}
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="w-full text-muted-foreground hover:text-foreground"
              onClick={handleBack}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t("resetPassword.back")}
            </Button>
          </CardFooter>
        </form>
      </Card>
    );
  }

  // Main login form
  return (
    <Card className="border-border bg-card/80 backdrop-blur animate-fade-in-up shadow-xl shadow-black/5">
      <CardHeader className="space-y-1 text-center pb-4">
        <div className="flex justify-center mb-4 lg:hidden">
          <div className="p-3 rounded-xl bg-cyan-500/10 border border-cyan-500/20">
            <Radar className="h-6 w-6 text-cyan-400" />
          </div>
        </div>
        <CardTitle className="text-2xl">{t("login.title")}</CardTitle>
        <CardDescription>{t("login.description")}</CardDescription>
      </CardHeader>
      <form onSubmit={handleSubmit}>
        <CardContent className="space-y-4">
          {resetSuccess && (
            <div className="p-3 text-sm text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 rounded-lg flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
              {t("resetPassword.success")}
            </div>
          )}
          {error && (
            <div className="p-3 text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="email">{t("login.email")}</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="email"
                type="email"
                placeholder={t("login.emailPlaceholder")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                className="bg-muted/50 border-border focus:border-cyan-500/50 pl-10"
              />
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="password">{t("login.password")}</Label>
              <button
                type="button"
                onClick={handleStartForgotPassword}
                className="text-xs text-cyan-400 hover:text-cyan-300 hover:underline font-medium"
              >
                {t("login.forgotPassword")}
              </button>
            </div>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                className="bg-muted/50 border-border focus:border-cyan-500/50 pl-10"
              />
            </div>
          </div>
        </CardContent>
        <CardFooter className="flex flex-col space-y-4">
          <Button
            type="submit"
            className="w-full bg-cyan-500 hover:bg-cyan-400 text-black font-semibold glow-cyan"
            disabled={isLoading}
          >
            {isLoading ? t("login.submitting") : t("login.submit")}
          </Button>
          <p className="text-sm text-center text-muted-foreground">
            {t("login.noAccount")}{" "}
            <Link to="/signup" className="text-cyan-400 hover:text-cyan-300 hover:underline font-medium">
              {t("login.signUp")}
            </Link>
          </p>
        </CardFooter>
      </form>
    </Card>
  );
}
